import React, { useEffect, useState } from "react";
import { DatePicker, InputNumber, Select } from "antd";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import { siteDayjs } from "../broccoliHarvestOptimiserDashboard/siteTimezone.js";
import { STATUS, STATUS_LABEL } from "./harvestEntryConstants.js";
import { convertKgToPound, convertPoundToKg } from "../../../utils/poundConversions.js";

const STATUS_OPTIONS = [
  { value: STATUS.PLANTED, label: STATUS_LABEL[STATUS.PLANTED] },
  { value: STATUS.HARVESTED, label: STATUS_LABEL[STATUS.HARVESTED] },
  { value: STATUS.DISCARDED, label: STATUS_LABEL[STATUS.DISCARDED] },
];

export const StatusEditCell = React.memo(function StatusEditCell({
  row,
  onRowChange,
  onClose,
  getEffectiveStatus,
  ensurePendingDate,
}) {
  const [open, setOpen] = useState(true);
  const current = getEffectiveStatus(row);

  useEffect(() => { setOpen(true); }, []);

  const apply = (next) => {
    const backendStatus = row.status ?? STATUS.PLANTED;

    if (next === STATUS.HARVESTED) {
      const base = ensurePendingDate({ ...row });
      onRowChange(
        { ...base, pendingStatus: next === backendStatus ? null : next },
        true,
      );
    } else if (next === STATUS.PLANTED || next === STATUS.DISCARDED) {
      onRowChange(
        {
          ...row,
          pendingStatus: next === backendStatus ? null : next,
          // clear pending yield/date only when reverting to planted
          ...(next === STATUS.PLANTED
            ? { pendingYield: null, pendingHarvestDate: null }
            : {}),
        },
        true,
      );
    }
  };

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Select
        value={current}
        style={{ width: "100%" }}
        className="harvest-cell-editor"
        variant="borderless"
        open={open}
        autoFocus
        options={STATUS_OPTIONS}
        getPopupContainer={() => document.body}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) requestAnimationFrame(() => onClose(true));
        }}
        onChange={(next) => {
          apply(next);
          setOpen(false);
        }}
      />
    </div>
  );
});

export const HarvestDateEditCell = React.memo(function HarvestDateEditCell({
  row,
  onRowChange,
  onClose,
  setDefaultHarvestDateISO,
  siteTimezone,
}) {
  const [open, setOpen] = useState(true);
  const raw = row.pendingHarvestDate || row.harvest_date || null;
  const value = raw ? siteDayjs(raw, siteTimezone) : null;

  useEffect(() => { setOpen(true); }, []);

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <DatePicker
        value={value}
        className="harvest-cell-editor"
        variant="borderless"
        allowClear
        format="DD MMM, YYYY"
        getPopupContainer={() => document.body}
        style={{ width: "100%" }}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          // Picker dismissed without selecting a date — commit current row as-is
          // so the editor closes cleanly.
          if (!nextOpen) requestAnimationFrame(() => onClose(true));
        }}
        onChange={(d) => {
          const next = d ? d.format(BACKEND_DATE_FORMAT) : null;
          const saved = row.harvest_date ?? null;

          if (next) setDefaultHarvestDateISO(next);

          onRowChange({ ...row, pendingHarvestDate: next === saved ? null : next }, true);
        }}
        inputReadOnly
        autoFocus
      />
    </div>
  );
});

export const YieldEditCell = React.memo(function YieldEditCell({
  row,
  onRowChange,
  onClose,
  ensurePendingDate,
  displayUnits,
}) {
  const savedValue = row.yield_kg ?? null;
  // pendingYield is stored in tons (canonical unit); convert to display units for input
  const displayValue =
    row.pendingYield !== null && row.pendingYield !== undefined
      ? (displayUnits === "imperial" ? convertKgToPound(row.pendingYield * 1000) : row.pendingYield)
      : (savedValue !== null ? (displayUnits === "imperial" ? convertKgToPound(savedValue * 1000) : savedValue) : null);

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <InputNumber
        value={displayValue}
        autoFocus
        min={displayUnits === "imperial" ? 2 : 0}
        precision={0}
        style={{ width: "100%" }}
        className="harvest-cell-editor"
        variant="borderless"
        onChange={(n) => {
          let nextRow = { ...row };

          if (n === null) {
            nextRow.pendingYield = null;
          } else {
            // Convert user input from display units to tons before storing
            const valueInTon = displayUnits === "imperial" ? (convertPoundToKg(n, { round: false }) / 1000) : n;
            const savedDisplayValue = savedValue !== null ? (displayUnits === "imperial" ? convertKgToPound(savedValue * 1000) : savedValue) : null;
            nextRow.pendingYield = n === savedDisplayValue ? null : valueInTon;

            if (nextRow.pendingYield !== null) {
              const effectiveStatus =
                row.pendingStatus ?? row.status ?? STATUS.PLANTED;
              if (effectiveStatus !== STATUS.HARVESTED) {
                nextRow.pendingStatus = STATUS.HARVESTED;
              }
              nextRow = ensurePendingDate(nextRow);
            }
          }

          onRowChange(nextRow);
        }}
        onBlur={() => onClose(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onClose(true); }
          if (e.key === "Escape") { e.preventDefault(); onClose(false); }
        }}
      />
    </div>
  );
});
